import { Badge, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * A custom-styled IconButton component.
 *
 * @param {boolean} [selected=false] - When true, the button appears in a selected state (changes background and icon color).
 * @param {"lg" | "md"} [variant="md"] - The size variant for the button. "lg" is larger than "md".
 *
 * @returns {JSX.Element} Styled IconButton with padding and size adjustments.
 */
export const StyledIconButton = styled(IconButton)<{
  selected?: boolean;
  variant?: "lg" | "md";
}>(({ selected, variant, theme }) => ({
  borderRadius: 8,
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  padding: variant === "lg" ? 12 : 8,
  width: variant === "lg" ? 48 : 40,
  height: variant === "lg" ? 48 : 40,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
  },
  boxShadow: "inset rgba(0, 0, 0, 0.1) 0px 0px 3px 0px",
}));

/**
 * A custom-styled Badge component.
 *
 * @param {"lg" | "md"} [badgeSize="md"] - Size of the badge ("lg" for larger badges, "md" for default).
 * @param {"dot"} [badgeVariant] - The style of the badge, "dot" for a small dot indicator.
 *
 * @returns {JSX.Element} Styled Badge component with dynamic size and variant.
 */
export const StyledBadge = styled(Badge)<{
  badgeSize?: "lg" | "md";
  badgeVariant?: "dot" | undefined;
}>(({ badgeSize, badgeVariant }) => ({
  "& .MuiBadge-badge": {
    ...(badgeVariant === "dot" && {
      width: badgeSize === "lg" ? 16 : 10,
      height: badgeSize === "lg" ? 16 : 10,
      borderRadius: "50%",
    }),
    ...(badgeVariant !== "dot" && {
      fontSize: badgeSize === "lg" ? "0.75rem" : "0.55rem",
      minWidth: badgeSize === "lg" ? 20 : 15,
      height: badgeSize === "lg" ? 20 : 15,
    }),
  },
}));
